import {Injectable} from '@angular/core';
import {AdminUserService} from '../admin/admin-user.service';
import {UserService} from '../customer/user.service';
import {AuthStorageService} from '../storage/auth-storage.service';
import {ProviderType} from "../../types/enums";

@Injectable({
  providedIn: 'root'
})
export class GuardService {

  constructor(
    private adminUserService: AdminUserService,
    private userService: UserService,
    private storageService: AuthStorageService
  ) {
  }

  async hasAccess(key: string): Promise<boolean> {
    try {
      return this[key + 'Access']();
    } catch (e) {
      try {
        return this[key]();
      } catch (e) {
        return false;
      }
    }
  }

  async ProfileEditAccess(): Promise<boolean> {
    return this.notGuest();
  }

  async AddressDetailAccess(): Promise<boolean> {
    return this.notGuest();
  }

  async BillingAddressDetailAccess(): Promise<boolean> {
    return this.notGuest();
  }

  async ProfileDeleteAccess(): Promise<boolean> {
    return await this.notGuest() && await this.notChip();
  }

  async PasswordResetAccess(): Promise<boolean> {
    const providerType = await this.storageService.getProviderType();
    return providerType === ProviderType.KIWI && await this.notGuest();
  }

  async SignupAccess(): Promise<boolean> {
    const isTerminal = await this.storageService.isTerminal();
    return !isTerminal;
  }

  async PushSettingAccess(): Promise<boolean> {
    return this.notGuest();
  }

  async ThermomixSettingAccess(): Promise<boolean> {
    return await this.notGuest() && await this.userService.isThermomixEnabled();
  }

  async AdminTerminalListAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminUserPaymentMethodEditAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminTerminalEditAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminTerminalGroupListAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminTerminalGroupEditAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminTerminalNavigationAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminNavigationAccess(): Promise<boolean> {
    return this.userService.hasRole();
  }

  public async AdminCompetitionEditAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  public async AdminAddressEditAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  public async AdminBillingAddressEditAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  public async AdminStoreSettingNavigationAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  public async AdminMasterDataNavigationAccess(): Promise<boolean> {
    return this.dataManagerAccess();
  }

  public async AdminProductEditAccess(): Promise<boolean> {
    return this.dataManagerAccess();
  }

  async AdminRecipeEditAccess(): Promise<boolean> {
    return this.dataManagerAccess();
  }

  async AdminRecipeImportAccess(): Promise<boolean> {
    return this.dataManagerAccess();
  }


  async AdminNutritionScoreEditAccess(): Promise<boolean> {
    return this.dataManagerAccess();
  }

  async AdminCategoryEditAccess(): Promise<boolean> {
    return this.dataManagerAccess();
  }

  async AdminOpeningHoursEditAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminStoreEditAccess(): Promise<boolean> {
    return await this.adminUserService.isAdmin();
  }

  async AdminSearchEditAccess(): Promise<boolean> {
    return this.dataManagerAccess();
  }

  async AdminRoleListAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminInventoryListAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminInventoryEditAccess(): Promise<boolean> {
    return this.storeManagerAccess(await this.getStore());
  }

  async AdminOpenTimeSlotElementListAccess(): Promise<boolean> {
    return this.storeManagerAccess(await this.getStore());
  }

  async AdminTimeSlotListAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminOpenPendingTimeSlotElementListAccess(): Promise<boolean> {
    return await this.stockEmployeeAccess() || await this.driverEmployeeAccess();
  }

  async AdminAppointmentDetailAccess(): Promise<boolean> {
    const store = await this.getStore();
    return await this.stockEmployeeAccess(store) || await this.driverEmployeeAccess(store);
  }

  async AdminAppointmentEditAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminOpenTimeSlotEditAccess(): Promise<boolean> {
    const store = await this.getStore();
    return await this.stockEmployeeAccess(store) || await this.driverEmployeeAccess(store);
  }

  async AdminOpenTimeSlotGroupEditAccess(): Promise<boolean> {
    const store = await this.getStore();
    return await this.stockEmployeeAccess(store) || await this.driverEmployeeAccess(store);
  }

  async AdminTimeSlotDetailAccess(): Promise<boolean> {
    return this.storeManagerAccess(await this.getStore());
  }

  async AdminTimeSlotGroupDetailAccess(): Promise<boolean> {
    return this.storeManagerAccess(await this.getStore());
  }

  async AdminTimeSlotGroupEditAccess(): Promise<boolean> {
    return this.storeManagerAccess(await this.getStore());
  }

  async AdminTimeSlotGroupRouteEditAccess(): Promise<boolean> {
    return this.driverEmployeeAccess(await this.getStore());
  }

  async AdminTimeSlotEditAccess(): Promise<boolean> {
    return this.storeManagerAccess(await this.getStore());
  }

  async AdminTimeSlotRouteEditAccess(): Promise<boolean> {
    return this.driverEmployeeAccess(await this.getStore());
  }


  async AdminTimeSlotSplitEdit(): Promise<boolean> {
    return this.storeManagerAccess(await this.getStore());
  }

  async AdminTimeSlotTypeListAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminTimeSlotTypeEditAccess(): Promise<boolean> {
    return this.storeManagerAccess(await this.getStore());
  }

  async AdminStockNavigationAccess(): Promise<boolean> {
    return this.stockEmployeeAccess();
  }

  async AdminStockListAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminStockEditAccess(): Promise<boolean> {
    return this.storeManagerAccess(await this.getStore());
  }

  async AdminInventoryLocationListAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminInventoryLocationEditAccess(): Promise<boolean> {
    return this.storeManagerAccess(await this.getStore());
  }

  async AdminStockIncomingEditAccess(): Promise<boolean> {
    return this.stockEmployeeAccess(await this.getStore());
  }

  async AdminStockLossCreateAccess(): Promise<boolean> {
    return this.stockEmployeeAccess(await this.getStore());
  }

  async AdminStockReturnEditAccess(): Promise<boolean> {
    return this.stockEmployeeAccess(await this.getStore());
  }

  async AdminStockOrderNavigationAccess(): Promise<boolean> {
    return this.stockEmployeeAccess()
  }

  async AdminStockOrderEditAccess(): Promise<boolean> {
    return this.stockEmployeeAccess(await this.getStore());
  }

  async AdminTaskNavigationAccess(): Promise<boolean> {
    return await this.stockEmployeeAccess() || await this.driverEmployeeAccess();
  }

  async AdminStorePackagingEditAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminOrderSearchAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminOrderListAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminOpenOrderListAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminOrderDetailAccess(): Promise<boolean> {
    return this.storeManagerAccess(await this.getStore());
  }

  async AdminOrderEditAccess(): Promise<boolean> {
    const store = await this.getStore();
    return await this.stockEmployeeAccess(store) || await this.driverEmployeeAccess(store);
  }

  async AdminEmployeeMyTaskAccess(): Promise<boolean> {
    const store = await this.getStore();
    return await this.stockEmployeeAccess(store) || await this.driverEmployeeAccess(store);
  }

  async AdminEmployeeListAccess(): Promise<boolean> {
    return this.storeManagerAccess(await this.getStore());
  }

  async AdminStatisticNavigationAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminSalesAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminRevenueAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminDeliveryStatisticAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminOrderDeleteAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminCouponNavigationAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminCouponEditAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminOfferNavigationAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminOfferEditAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminNewsNavigationAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminNewsEditAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminNewsListAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminDoneNewsListAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminMissingInvoiceListAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminStoreNewsListAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminPrinterAccess(): Promise<boolean> {
    return this.storeManagerAccess();
  }

  async AdminUserEditAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminUserListAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminUserOrderListAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminUserOrderDetailAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminMandateEditAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminHealthCheckAccess(): Promise<boolean> {
    return this.superAdminAccess();
  }

  async AdminDeliverySimulatorAccess(): Promise<boolean> {
    return this.adminUserService.isAdmin();
  }

  async AdminThumbnailAccess(): Promise<boolean> {
    return this.superAdminAccess();
  }

  private async dataManagerAccess(): Promise<boolean> {
    return (await this.adminUserService.isAdmin()) || (await this.adminUserService.isDataManager());
  }

  private async storeManagerAccess(storeId?: string): Promise<boolean> {
    return (await this.adminUserService.isAdmin()) || (await this.adminUserService.isStoreManager(storeId));
  }

  private async stockEmployeeAccess(storeId?: string): Promise<boolean> {
    return (await this.adminUserService.isStockEmployee(storeId)) || (await this.storeManagerAccess(storeId));;
  }

  private async driverEmployeeAccess(storeId?: string): Promise<boolean> {
    return (await this.adminUserService.isDriver(storeId)) || (await this.storeManagerAccess(storeId));
  }

  private async superAdminAccess(): Promise<boolean> {
    const mail = await this.adminUserService.getMail();
    return await this.adminUserService.isAdmin()
      && (mail || '').indexOf('@youco-it.com') > 0;
  }

  private async getStore(): Promise<string> {
    return this.storageService.getEmployeeStoreId()
  }

  private async notGuest(): Promise<boolean> {
    const isGuest = await this.storageService.isGuest();
    return !isGuest;
  }

  private async notChip(): Promise<boolean> {
    const isChip = await this.storageService.isChip();
    return !isChip;
  }
}
